import React, {
  lazy,
  Suspense,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import CloseIcon from './CloseIcon';
import CandidateHeader from './CandidateHeader';
// import CandidateChat from './Chat';
import Details from './Details';
import Notes from './Notes';
import AssessmentAndInterview from './AssessmentAndInterview';
import CandidateTimeline from './CandidateTimeline';
import EventDetails from './EventDetails';
import JobSpecs from './JobSpecs';
import CandidatesService from '../../../services/CandidatesService';
import AdminService from '../../../services/AdminService';
import Applications from './Applications';
import classes from './styles.module.scss';
import { useUserStore } from '../../../store';
import FeedbackContent from '../../FeedbackContent';
import UserService from '../../../services/UserService';
import { hasAuthData } from '../../../helpers/authStorage';
import PagindateItems from './PagindateItems';

const WordResume = lazy(() => import('./WordResume'));

export default function CandidateDetails({
  isVisible,
  setIsVisible,
  candidate,
  refetch,
  jobApplicationId = null,
  event = null,
  itemIds = [],
  setCurrentItemId,
}) {
  const isClient = useUserStore((state) => state.isClient);
  const isInterviewer = useUserStore((state) => state.isInterviewer);
  const companyId = useUserStore((state) => state.companyId);

  // Memoize screenType to avoid recalculation on every render
  const screenType = useMemo(() => {
    if (isClient) return 'Client';
    if (isInterviewer) return 'Interviewer';
    return 'Normal';
  }, [isClient, isInterviewer]);

  const [currentForm, setCurrentForm] = useState('Event Details');
  const [isOpened, setIsOpened] = useState(false);

  const { t } = useTranslation();

  // Include candidate?.id in query keys to ensure refetch when candidate changes
  const {
    data: jobApplication,
    refetch: refetchJobApplication,
    isLoading: isLoadingJobApplication,
  } = useQuery({
    queryKey: ['jobApplication', jobApplicationId, candidate?.id],
    queryFn: () => AdminService.getJobApplication({ id: jobApplicationId }),
    enabled: !!jobApplicationId && isVisible && !!candidate?.id,
  });

  const {
    data: job,
    refetch: refetchJob,
    isLoading: isLoadingJob,
  } = useQuery({
    queryKey: [
      'job',
      jobApplication?.jobPost?.id,
      candidate?.id,
      jobApplicationId,
    ],
    queryFn: () => AdminService.getJobPost({ id: jobApplication?.jobPost?.id }),
    enabled: !!jobApplication?.jobPost?.id && isVisible && !!candidate?.id,
  });

  // Use the interview from jobApplication if available (fresher data), otherwise fall back to event prop
  const currentEvent = useMemo(() => {
    if (!event) return null;

    // Try to find the same interview in the fetched jobApplication data (fresher data)
    if (jobApplication?.jobApplicationInterviews) {
      const freshInterview = jobApplication.jobApplicationInterviews.find(
        (interview) => interview.id === event.id
      );
      if (freshInterview) return freshInterview;
    }

    // Fall back to the event prop if not found in jobApplication
    return event;
  }, [event, jobApplication?.jobApplicationInterviews]);

  // Memoize currentEvent derivation logic for ClientInterview status
  const derivedCurrentEvent = useMemo(() => {
    // if (!isClient && !isInterviewer) return null;
    if (
      jobApplication?.status === 'ClientInterview' &&
      !currentEvent &&
      jobApplication?.jobApplicationInterviews
    ) {
      const newEvent =
        jobApplication.jobApplicationInterviews.find(
          (interview) =>
            interview.status !== 'Canceled' &&
            interview.contactType === 'Client'
        ) ||
        jobApplication.jobApplicationInterviews.find(
          (interview) => interview.contactType === 'Client'
        );
      return newEvent || currentEvent;
    }
    return currentEvent;
  }, [
    jobApplication?.status,
    jobApplication?.jobApplicationInterviews,
    currentEvent,
  ]);

  const {
    data: jobApplicationsSource,
    refetch: refetchJobApplications,
    isLoading: isLoadingJobApplications,
  } = useQuery({
    queryKey: ['jobApplications', candidate?.id],
    queryFn: () => {
      const filters = isClient
        ? [`jobApplication.user.id:eq:${candidate?.id}`]
        : [`user.id:eq:${candidate?.id}`];
      if (isClient) {
        return AdminService.getCompanyJobApplications({
          pageSize: 9999,
          companyId,
          Filters: filters,
        });
      }
      return AdminService.getUserJobApplications({
        Filters: filters,
      });
    },
    enabled: !!candidate?.id && isVisible,
  });

  const refechAll = useCallback(() => {
    refetch();
    refetchJob();
    refetchJobApplication();
    refetchJobApplications();
  }, [refetch, refetchJob, refetchJobApplication, refetchJobApplications]);

  const jobApplications = jobApplicationsSource?.data;

  // Memoize cleanJobApplications to avoid recalculation on every render
  const cleanJobApplications = useMemo(() => {
    return isClient
      ? jobApplications?.map((application) => ({
          ...application.jobApplication,
        }))
      : jobApplications;
  }, [jobApplications, isClient]);

  const { data: careerInfo, isLoading: isLoadingCareerInfo } = useQuery({
    queryKey: ['candaidateInfo', candidate?.id],
    queryFn: () => CandidatesService.getCandidateInfo(candidate?.id),
    enabled: !!candidate?.id && isVisible,
  });

  // Determine if critical data is loading for candidateInfo section
  const isCandidateInfoLoading = useMemo(() => {
    // If jobApplicationId exists, wait for jobApplication and job
    if (jobApplicationId) {
      return (
        isLoadingCareerInfo ||
        isLoadingJobApplication ||
        (!!jobApplication?.jobPost?.id && isLoadingJob)
      );
    }
    // Otherwise, wait for careerInfo and jobApplications
    return isLoadingCareerInfo || isLoadingJobApplications;
  }, [
    isLoadingCareerInfo,
    isLoadingJobApplication,
    isLoadingJob,
    isLoadingJobApplications,
    jobApplicationId,
    jobApplication?.jobPost?.id,
  ]);

  // Memoize forms array to avoid recalculation on every render
  const forms = useMemo(() => {
    let baseForms;

    if (jobApplicationId) {
      if (screenType === 'Client') {
        baseForms = [
          { tab: 'Candidate Details' },
          { tab: 'Assessments' },
          { tab: 'Word Resume' },
          { tab: 'Job Specs' },
          { tab: 'Timeline' },
          { tab: 'Applications', amount: jobApplications?.length },
        ];
      } else {
        baseForms = [
          { tab: 'Candidate Details' },
          { tab: 'Word Resume' },
          // { tab: 'Chat', amount: jobApplicationThread?.totalCount || 0 },
          { tab: 'Assessment & Interview' },
          { tab: 'Notes', amount: jobApplication?.jobApplicationNotes?.length },
          { tab: 'Timeline' },
        ];
      }
    } else if (screenType === 'Client') {
      baseForms = [
        { tab: 'Candidate Details' },
        { tab: 'Word Resume' },
        { tab: 'Applications', amount: jobApplications?.length },
      ];
    } else {
      baseForms = [
        { tab: 'Candidate Details' },
        { tab: 'Word Resume' },
        { tab: 'Applications', amount: jobApplications?.length },
      ];
    }

    if (screenType === 'Interviewer') {
      baseForms = [
        { tab: 'Candidate Details' },
        { tab: 'Word Resume' },
        { tab: 'Job Specs' },
      ];
    }

    // Create a new array instead of mutating
    let resultForms = [...baseForms];

    // Add Event Details tab if derivedCurrentEvent exists
    if (derivedCurrentEvent) {
      resultForms = [{ tab: 'Event Details' }, ...resultForms];
    }

    // Add Feedback tab if conditions are met
    if (
      derivedCurrentEvent &&
      (derivedCurrentEvent?.contactType === 'Client' ||
        derivedCurrentEvent?.contactType === 'External') &&
      derivedCurrentEvent?.interviewDate &&
      derivedCurrentEvent?.status !== 'Canceled' &&
      new Date(derivedCurrentEvent?.interviewDate) < new Date()
    ) {
      resultForms = [{ tab: 'Feedback' }, ...resultForms];
    }

    return resultForms;
  }, [
    jobApplicationId,
    screenType,
    jobApplications?.length,
    derivedCurrentEvent,
    jobApplication?.jobApplicationNotes?.length,
  ]);

  const isAuthorized = hasAuthData();

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
    enabled: isAuthorized,
  });

  const feedback = useMemo(() => {
    return derivedCurrentEvent?.jobApplicationInterviewFeedbacks?.find(
      (fb) => fb.userId === me?.id
    );
  }, [derivedCurrentEvent?.jobApplicationInterviewFeedbacks, me?.id]);

  useEffect(() => {
    // Set initial form based on available forms and currentEvent
    if (derivedCurrentEvent) {
      if (forms.some((tab) => tab.tab === 'Feedback')) {
        setCurrentForm('Feedback');
      } else {
        setCurrentForm('Event Details');
      }
    } else {
      setCurrentForm('Candidate Details');
    }
    if (jobApplicationId && candidate?.id) {
      AdminService.addCandidateToViewed({ id: candidate?.id });
    }
  }, [candidate?.id, jobApplicationId, derivedCurrentEvent, forms]);

  useEffect(() => {
    if (isVisible) {
      setIsOpened(true);
    }
  }, [isVisible]);

  const handleClose = () => {
    setIsOpened(false);

    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };
  return (
    <>
      <div
        className={classNames(classes.blur, {
          [classes.blurActivated]: isVisible,
        })}
      >
        <div
          className={classNames(classes.container, {
            [classes.visible]: isOpened,
          })}
        >
          <CandidateHeader
            candidate={candidate}
            refetch={refechAll}
            jobApplication={jobApplication}
            job={job}
            isVisible={isVisible}
            handleClose={handleClose}
            event={derivedCurrentEvent}
            careerInfo={careerInfo}
          />
          <div
            className={classNames(classes.candidateInfo, {
              [classes.loading]: isCandidateInfoLoading,
            })}
          >
            <ul className={classNames(classes.links, classes.withoutGap)}>
              {forms.map((form) =>
                ['Notes', 'Applications', 'Chat'].includes(form.tab) ? (
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                  <li
                    className={classNames(classes.link, {
                      [classes.linkActive]: form.tab === currentForm,
                    })}
                    onClick={() => setCurrentForm(form.tab)}
                    key={form.tab}
                  >
                    {t(
                      `dashboardComponents.Candidate.${form.tab.toLowerCase()}`
                    )}
                    <span
                      className={classNames(classes.counter, {
                        [classes.counterActive]:
                          (form.tab === currentForm &&
                            currentForm === 'Notes') ||
                          (form.tab === currentForm &&
                            currentForm === 'Applications'),
                        [classes.noData]: !form.amount,
                      })}
                    >
                      {form.amount}
                    </span>
                  </li>
                ) : (
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                  <li
                    className={classNames(classes.link, {
                      [classes.linkActive]: form.tab === currentForm,
                    })}
                    onClick={() => setCurrentForm(form.tab)}
                    key={form.tab}
                  >
                    {t(
                      `dashboardComponents.Candidate.${form.tab.toLowerCase()}`
                    )}
                  </li>
                )
              )}
            </ul>

            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Candidate Details',
              })}
            >
              <Details
                candidate={candidate}
                currentForm={currentForm}
                setCurrentForm={setCurrentForm}
                sidePopupIsOpened={isOpened}
                jobApplication={jobApplication}
                jobApplications={jobApplications}
                careerInfo={careerInfo}
                job={job}
              />
            </div>
            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Word Resume',
              })}
            >
              <Suspense
                fallback={
                  <div>
                    {t('dashboardComponents.Candidate.WordResume.Word.loading')}
                  </div>
                }
              >
                <WordResume
                  candidate={candidate}
                  currentForm={currentForm}
                  careerInfoSource={careerInfo}
                />
              </Suspense>
            </div>
            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Assessment & Interview',
              })}
            >
              <AssessmentAndInterview
                jobApplication={jobApplication}
                job={job}
                refetch={refechAll}
              />
            </div>

            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Assessments',
              })}
            >
              <AssessmentAndInterview
                jobApplication={jobApplication}
                job={job}
                refetch={refechAll}
              />
            </div>

            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Event Details',
              })}
            >
              {currentForm === 'Event Details' && (
                <EventDetails
                  event={derivedCurrentEvent}
                  jobPost={job}
                  jobApplication={jobApplication}
                  refetch={refechAll}
                />
              )}
            </div>
            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Feedback',
              })}
            >
              {currentForm === 'Feedback' && (
                <FeedbackContent
                  candidate={jobApplication}
                  interview={derivedCurrentEvent}
                  job={job}
                  refetch={refechAll}
                  existingFeedback={feedback}
                  isReviewing={false}
                  onClose={() => {}}
                  showCloseButton={false}
                />
              )}
            </div>
            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Job Specs',
              })}
            >
              {currentForm === 'Job Specs' && job && <JobSpecs job={job} />}
            </div>
            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Notes',
              })}
            >
              <Notes
                jobApplication={jobApplication}
                refetch={refechAll}
                isVisible={currentForm === 'Notes'}
              />
            </div>
            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Timeline',
              })}
            >
              {candidate?.id && (
                <CandidateTimeline candidate={jobApplication} />
              )}
            </div>
            <div
              className={classNames(classes.hidden, {
                [classes.open]: currentForm === 'Applications',
              })}
            >
              <Applications jobApplications={cleanJobApplications} />
            </div>
          </div>
          {itemIds?.length > 1 && (
            <PagindateItems
              itemId={jobApplicationId || candidate?.id}
              itemIds={itemIds}
              setCurrentItemId={setCurrentItemId}
            />
          )}
          <div className={classes.closeIconContainer}>
            <CloseIcon onClick={handleClose} />
          </div>
        </div>
      </div>
    </>
  );
}
